<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  // 当前 tab: 'documents' | 'faqs' | 'recall_test'
  currentTab: {
    type: String,
    default: 'documents',
  },
  // 是否折叠
  collapsed: {
    type: Boolean,
    default: false,
  },
  // 知识库信息
  knowledgeBase: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['select', 'toggle-collapse', 'tab-change']);

const { t } = useI18n();

// hover 状态
const hoverKey = ref(null);

// Tab 导航的颜色
const tabColors = {
  documents: '#14A2B5',
  faqs: '#992AFF',
  recall_test: '#4B52FA',
};

// 快捷操作的颜色
const actionColors = {
  uploadDocument: '#14A2B5',
  crawlWechat: '#2DD4BF',
  syncMarketingCloud: '#EAB308',
  crawlWebpage: '#FE6206',
  addFaq: '#992AFF',
  importFaq: '#10B981',
};

// Tab 导航项
const tabItems = computed(() => [
  {
    id: 'documents',
    icon: 'i-lucide-file-text',
    label: t('PILOT.KNOWLEDGE_BASES.TABS.DOCUMENTS'),
    type: 'tab',
  },
  {
    id: 'faqs',
    icon: 'i-lucide-message-circle-question',
    label: t('PILOT.KNOWLEDGE_BASES.TABS.FAQS'),
    type: 'tab',
  },
  {
    id: 'recall_test',
    icon: 'i-lucide-flask-conical',
    label: t('PILOT.KNOWLEDGE_BASES.TABS.RECALL_TEST'),
    type: 'tab',
  },
]);

// 添加文档知识方式
const documentActions = computed(() => [
  {
    id: 'uploadDocument',
    icon: 'i-ri-file-word-2-line',
    label: t('PILOT.KNOWLEDGE_BASES.SIDEBAR.UPLOAD_DOCUMENT'),
    action: 'uploadDocument',
  },
  {
    id: 'crawlWebpage',
    icon: 'i-lucide-globe',
    label: t('PILOT.KNOWLEDGE_BASES.SIDEBAR.CRAWL_WEBPAGE'),
    action: 'crawlWebpage',
  },
  {
    id: 'crawlWechat',
    icon: 'i-ri-wechat-2-line',
    label: t('PILOT.KNOWLEDGE_BASES.SIDEBAR.CRAWL_WECHAT'),
    action: 'crawlWechat',
    developmentToast: true,
  },
  {
    id: 'syncMarketingCloud',
    icon: 'i-lucide-cloud-check',
    label: t('PILOT.KNOWLEDGE_BASES.SIDEBAR.SYNC_MARKETING_CLOUD'),
    action: 'syncMarketingCloud',
    developmentToast: true,
  },
]);

// 添加 FAQs 知识方式
const faqActions = computed(() => [
  {
    id: 'addFaq',
    icon: 'i-lucide-message-circle-question',
    label: t('PILOT.KNOWLEDGE_BASES.SIDEBAR.ADD_FAQ'),
    action: 'addFaq',
  },
  {
    id: 'importFaq',
    icon: 'i-lucide-file-spreadsheet',
    label: t('PILOT.KNOWLEDGE_BASES.SIDEBAR.IMPORT_FAQ'),
    action: 'importFaq',
  },
]);

const handleItemClick = item => {
  if (item.type === 'sectionHeader' || item.type === 'logo' || item.disabled)
    return;

  // 显示开发中提示
  if (item.developmentToast) {
    useAlert(t('PILOT.KNOWLEDGE_BASES.SIDEBAR.COMING_SOON'));
    return;
  }

  if (item.type === 'tab') {
    emit('tab-change', item.id);
  } else {
    emit('select', item);
  }
};

const handleToggle = () => {
  emit('toggle-collapse');
};

const isTabActive = item => {
  return item.type === 'tab' && props.currentTab === item.id;
};

const isActive = item => {
  return props.activeItem === item.id;
};

const getLogoColor = name => {
  const colors = [
    '#8B5CF6',
    '#3B82F6',
    '#10B981',
    '#F59E0B',
    '#EF4444',
    '#EC4899',
    '#6366F1',
  ];
  const index = (name?.charCodeAt(0) || 0) % colors.length;
  return colors[index];
};

const getItemColor = item => {
  if (item.disabled) return '#9CA3AF';
  if (item.type === 'tab') return tabColors[item.id] || '#4B52FA';
  return actionColors[item.id] || '#4B52FA';
};
</script>

<template>
  <nav
    class="flex flex-col h-full bg-[#F7F7F7] dark:bg-n-slate-900 rounded-xl transition-all duration-300"
    :class="collapsed ? 'w-16 px-1' : 'w-44 px-2'"
  >
    <!-- 导航菜单 -->
    <div class="flex-1 py-4 overflow-y-auto">
      <!-- 展开状态 -->
      <ul v-if="!collapsed" class="flex flex-col gap-1">
        <!-- Logo 区域 -->
        <li class="pb-2 mb-2">
          <img
            v-if="knowledgeBase?.logo_url"
            :src="knowledgeBase.logo_url"
            :alt="knowledgeBase.name"
            class="w-full rounded-xl"
          />
          <div
            v-else
            class="w-full aspect-square rounded-xl flex items-center justify-center"
            :style="{ backgroundColor: getLogoColor(knowledgeBase?.name) }"
          >
            <span class="text-4xl font-bold text-white">
              {{ knowledgeBase?.name?.charAt(0)?.toUpperCase() || '?' }}
            </span>
          </div>
        </li>

        <!-- 内容管理 分组标题 -->
        <li>
          <div class="px-3 pt-3 pb-2">
            <span
              class="text-[11px] font-medium text-n-slate-9 uppercase tracking-wider"
            >
              {{ t('PILOT.KNOWLEDGE_BASES.SIDEBAR.CONTENT_MANAGEMENT') }}
            </span>
          </div>
        </li>

        <!-- Tab 导航项 -->
        <li v-for="item in tabItems" :key="item.id">
          <button
            type="button"
            class="w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-all"
            :class="
              isTabActive(item) || hoverKey === item.id
                ? 'bg-white dark:bg-n-slate-800 shadow-sm'
                : ''
            "
            @click="handleItemClick(item)"
            @mouseenter="hoverKey = item.id"
            @mouseleave="hoverKey = null"
          >
            <span
              class="text-[16px] flex-shrink-0 transition-colors"
              :class="[item.icon]"
              :style="{ color: getItemColor(item) }"
            />
            <span
              class="text-[13px] font-medium truncate transition-colors"
              :class="
                isTabActive(item)
                  ? 'text-n-slate-12 dark:text-white'
                  : 'text-n-slate-11'
              "
            >
              {{ item.label }}
            </span>
          </button>
        </li>

        <!-- 添加文档知识方式 分组标题 -->
        <li>
          <div class="px-3 pt-5 pb-2">
            <span
              class="text-[11px] font-medium text-n-slate-9 uppercase tracking-wider"
            >
              {{ t('PILOT.KNOWLEDGE_BASES.SIDEBAR.ADD_KNOWLEDGE') }}
            </span>
          </div>
        </li>

        <!-- 文档快捷操作 -->
        <li v-for="item in documentActions" :key="item.id">
          <button
            type="button"
            class="w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-all group relative"
            :class="[
              item.disabled ? 'cursor-not-allowed' : '',
              isActive(item) || hoverKey === item.id
                ? 'bg-white dark:bg-n-slate-800 shadow-sm'
                : '',
            ]"
            @click="handleItemClick(item)"
            @mouseenter="hoverKey = item.id"
            @mouseleave="hoverKey = null"
          >
            <span
              class="text-[16px] flex-shrink-0 transition-colors"
              :class="[item.icon, { 'opacity-40': item.disabled }]"
              :style="{ color: getItemColor(item) }"
            />
            <span
              class="text-[13px] font-medium truncate transition-colors"
              :class="[
                item.disabled
                  ? 'text-n-slate-9'
                  : 'text-n-slate-11',
              ]"
            >
              {{ item.label }}
            </span>
          </button>
        </li>

        <!-- 添加 FAQs 知识方式 分组标题 -->
        <li>
          <div class="px-3 pt-5 pb-2">
            <span
              class="text-[11px] font-medium text-n-slate-9 uppercase tracking-wider"
            >
              {{ t('PILOT.KNOWLEDGE_BASES.SIDEBAR.ADD_FAQ_KNOWLEDGE') }}
            </span>
          </div>
        </li>

        <!-- FAQ 快捷操作 -->
        <li v-for="item in faqActions" :key="item.id">
          <button
            type="button"
            class="w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-all"
            :class="
              isActive(item) || hoverKey === item.id
                ? 'bg-white dark:bg-n-slate-800 shadow-sm'
                : ''
            "
            @click="handleItemClick(item)"
            @mouseenter="hoverKey = item.id"
            @mouseleave="hoverKey = null"
          >
            <span
              class="text-[16px] flex-shrink-0 transition-colors"
              :class="[item.icon]"
              :style="{ color: getItemColor(item) }"
            />
            <span class="text-[13px] font-medium truncate text-n-slate-11">
              {{ item.label }}
            </span>
          </button>
        </li>
      </ul>

      <!-- 收起状态 -->
      <ul v-else class="flex flex-col items-center gap-3">
        <!-- Logo 区域 -->
        <li>
          <div
            class="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0"
            :class="
              knowledgeBase?.logo_url
                ? ''
                : 'flex items-center justify-center'
            "
          >
            <img
              v-if="knowledgeBase?.logo_url"
              :src="knowledgeBase.logo_url"
              :alt="knowledgeBase.name"
              class="w-full h-full object-cover"
            />
            <span
              v-else
              class="text-lg font-bold text-white"
              :style="{ backgroundColor: getLogoColor(knowledgeBase?.name) }"
            >
              {{ knowledgeBase?.name?.charAt(0)?.toUpperCase() || '?' }}
            </span>
          </div>
        </li>

        <!-- 分隔线 -->
        <li>
          <div class="w-10 my-1 border-t border-n-slate-4 dark:border-n-slate-7" />
        </li>

        <!-- Tab 导航项（收起） -->
        <li v-for="item in tabItems" :key="item.id">
          <button
            type="button"
            class="flex flex-col items-center gap-1 relative group"
            @click="handleItemClick(item)"
            @mouseenter="hoverKey = item.id"
            @mouseleave="hoverKey = null"
          >
            <!-- 折叠时的 tooltip -->
            <div
              class="absolute left-full ml-2 z-50 px-2 py-1 text-xs text-white bg-[#374151] rounded whitespace-nowrap shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
            >
              {{ item.label }}
              <div
                class="absolute right-full top-1/2 -translate-y-1/2 border-[6px] border-transparent border-r-[#374151]"
              />
            </div>

            <div
              class="p-[5px] rounded-lg transition-all duration-150 ease-out"
              :class="
                isTabActive(item) || hoverKey === item.id
                  ? 'shadow-[0_2px_8px_rgba(0,0,0,0.15)] bg-white dark:bg-n-slate-800'
                  : ''
              "
            >
              <span
                class="text-[16px] block"
                :class="[item.icon]"
                :style="{ color: getItemColor(item) }"
              />
            </div>
            <span
              class="text-[10px] font-medium w-full text-center leading-tight overflow-hidden"
              :class="
                isTabActive(item)
                  ? 'text-n-slate-12 dark:text-white'
                  : 'text-n-slate-11'
              "
              style="
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
              "
            >
              {{ item.label }}
            </span>
          </button>
        </li>

        <!-- 分隔线 -->
        <li>
          <div class="w-10 my-1 border-t border-n-slate-4 dark:border-n-slate-7" />
        </li>

        <!-- 文档快捷操作（收起） -->
        <li v-for="item in documentActions" :key="item.id">
          <button
            type="button"
            class="flex flex-col items-center gap-1 relative group"
            :class="{ 'cursor-not-allowed': item.disabled }"
            @click="handleItemClick(item)"
            @mouseenter="hoverKey = item.id"
            @mouseleave="hoverKey = null"
          >
            <div
              class="absolute left-full ml-2 z-50 px-2 py-1 text-xs text-white bg-[#374151] rounded whitespace-nowrap shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
            >
              <span :class="{ 'text-n-slate-4': item.disabled }">{{
                item.label
              }}</span>
              <div
                class="absolute right-full top-1/2 -translate-y-1/2 border-[6px] border-transparent border-r-[#374151]"
              />
            </div>
            <div class="p-[5px] rounded-lg transition-all duration-150">
              <span
                class="text-[16px] block"
                :class="[item.icon, { 'opacity-40': item.disabled }]"
                :style="{ color: getItemColor(item) }"
              />
            </div>
            <span
              class="text-[10px] font-medium w-full text-center leading-tight overflow-hidden text-n-slate-11"
              style="
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
              "
            >
              {{ item.label }}
            </span>
          </button>
        </li>

        <!-- FAQ 快捷操作（收起） -->
        <li v-for="item in faqActions" :key="item.id">
          <button
            type="button"
            class="flex flex-col items-center gap-1 relative group"
            @click="handleItemClick(item)"
            @mouseenter="hoverKey = item.id"
            @mouseleave="hoverKey = null"
          >
            <div
              class="absolute left-full ml-2 z-50 px-2 py-1 text-xs text-white bg-[#374151] rounded whitespace-nowrap shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
            >
              {{ item.label }}
              <div
                class="absolute right-full top-1/2 -translate-y-1/2 border-[6px] border-transparent border-r-[#374151]"
              />
            </div>
            <div class="p-[5px] rounded-lg transition-all duration-150">
              <span
                class="text-[16px] block"
                :class="[item.icon]"
                :style="{ color: getItemColor(item) }"
              />
            </div>
            <span
              class="text-[10px] font-medium w-full text-center leading-tight overflow-hidden text-n-slate-11"
              style="
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
              "
            >
              {{ item.label }}
            </span>
          </button>
        </li>
      </ul>
    </div>

    <!-- 底部折叠按钮 -->
    <div class="py-3 border-t border-n-slate-4 dark:border-n-slate-7">
      <button
        type="button"
        class="w-full flex items-center justify-center gap-2 py-1.5 text-n-slate-10 hover:text-n-slate-12 transition-colors"
        @click="handleToggle"
      >
        <span
          :class="
            collapsed ? 'i-lucide-panel-left-close' : 'i-lucide-panel-left'
          "
          class="text-lg"
        />
        <span v-if="!collapsed" class="text-xs">
          {{ t('PILOT.KNOWLEDGE_BASES.SIDEBAR.COLLAPSE') }}
        </span>
      </button>
    </div>
  </nav>
</template>
